import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WeatherData } from '../models/Models';
import { Translation } from '../helper/WinfireLiveConstHelper';
import './WeatherLowerThird.css';

// Location pin path (Material Design location_on icon, 24×24 viewBox)
const LocationPinPath = "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z";

interface WeatherLowerThirdProps {
    locationName: string | undefined | null;
    weatherData: WeatherData | undefined | null;
    /** When true the overlay slides in; when false it slides out. */
    visible: boolean;
}

/**
 * Broadcast-style lower-third overlay — shown during the Preparation (Probeschüsse) phase.
 *
 * Layout:
 *                                     ──── [ LIVE ]   ← accent line + badge (top-right)
 *   ╭────────┬──────────────────────────────────────╮
 *   │ [📍]   │  LOCATION NAME  (max 2 lines)        │  ← yellow pin box + light headline
 *   │        │  WeatherDescription  (temp font size) │     (rounded top left corner)
 *   ├────────┴──────────────────────────────────────┤
 *   │  HH:MM  –  Temp °C  Wind km/h                 │  ← dark info strip
 *   └───────────────────────────────────────────────┘
 */
function WeatherLowerThird({ locationName, weatherData, visible }: WeatherLowerThirdProps) {
    const { t } = useTranslation();

    // Synchronous init avoids a one-frame empty-string flash before first effect tick.
    const [currentTime, setCurrentTime] = useState<string>(() =>
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    );

    // Track whether overlay has ever been shown, so we only play the slide-out animation
    // after the first appearance — not on the initial (hidden) render.
    const hasBeenVisible = useRef(false);
    useEffect(() => {
        if (visible) {
            hasBeenVisible.current = true;
        }
    }, [visible]);

    // Clock ticker — clean up on unmount.
    useEffect(() => {
        const tick = () =>
            setCurrentTime(
                new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            );
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    const animClass = visible
        ? 'wlt-visible'
        : hasBeenVisible.current
            ? 'wlt-hidden'
            : '';

    const hasWeather = !!(
        weatherData?.temperatureCelsius != null ||
        weatherData?.windSpeedKmH != null ||
        weatherData?.weatherDescription
    );

    return (
        <div className="wlt-container" aria-hidden={!visible ? true : undefined}>
            <div className={`wlt-inner ${animClass}`}>

                {/* ── Top accent bar: accent line + LIVE badge (right-aligned) ── */}
                <div className="wlt-top-bar">
                    <div className="wlt-live-row">
                        <div className="wlt-live-badge">LIVE</div>
                    </div>
                </div>

                {/* ── Upper strip: yellow pin box + headline — rounded top corners ── */}
                <div className="wlt-upper-strip">
                    {/* Location pin icon replaces the logo in the yellow cell */}
                    <div className="wlt-logo-cell">
                        <svg className="wlt-logo-pin" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d={LocationPinPath} />
                        </svg>
                    </div>
                    <div className="wlt-headline-area">
                        {/* Location name — wraps to max 2 lines */}
                        <div className="wlt-location-line">
                            <span
                                className="wlt-location-name"
                                title={locationName ?? undefined}
                            >
                                {locationName ?? ''}
                            </span>
                        </div>

                        {/* Weather description — 3rd line, temperature font size */}
                        {weatherData?.weatherDescription && (
                            <div
                                className="wlt-headline-desc"
                                title={weatherData.weatherDescription}
                            >
                                {weatherData.weatherDescription}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Lower data strip: Time + Weather (temp + wind only) ── */}
                <div className="wlt-data-strip">
                    <span className="wlt-time">{currentTime}</span>

                    {hasWeather && (
                        <>
                            <span className="wlt-sep">–</span>
                            <div className="wlt-weather">
                                {weatherData?.temperatureCelsius != null && (
                                    <span className="wlt-weather-item">
                                        <span className="wlt-weather-label">
                                            {t(Translation.WEATHER_TEMPERATURE)}
                                        </span>
                                        <span className="wlt-weather-value">
                                            {weatherData.temperatureCelsius.toFixed(1)}°C
                                        </span>
                                    </span>
                                )}

                                {weatherData?.windSpeedKmH != null && (
                                    <span className="wlt-weather-item">
                                        <span className="wlt-weather-label">
                                            {t(Translation.WEATHER_WIND)}
                                        </span>
                                        <span className="wlt-weather-value">
                                            {weatherData.windSpeedKmH.toFixed(1)} km/h
                                        </span>
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
}

export default WeatherLowerThird;

